import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  None,
} from 'dtos';
import {
  deleteApiPatientRecentSearches,
  getApiPatientRecentSearches,
  postApiPatientRecordChartAccessPatientId,
} from 'endpoints';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  formatDate,
} from 'utils/date';

export const recentSearchesQueryKeys = {
  all: ['recent-searches'] as const,
};

export function useRecentSearches(
  enabled: boolean = true,
): UseQueryResult<PatientCompact[], Error> {
  return useQuery({
    staleTime: 5 * 60 * 1000,
    queryKey: recentSearchesQueryKeys.all,
    queryFn: async ({ signal }) => {
      const data = await getApiPatientRecentSearches({ signal });
      return data.map((chart) => ({
        ...chart,
        formattedDateOfBirth: formatDate(chart.dateOfBirth ?? ''),
      }));
    },
    enabled,
  });
}

export function useRecordChartAccess(): UseMutationResult<None, Error, number> {
  const queryClient = useQueryClient();

  return useMutation<None, Error, number>({
    mutationFn: async (patientId: number) => {
      const result = await postApiPatientRecordChartAccessPatientId(patientId);
      await queryClient.invalidateQueries({
        queryKey: recentSearchesQueryKeys.all,
      });
      return result;
    },
  });
}

export function useClearRecentSearches(): UseMutationResult<None, Error, void> {
  const queryClient = useQueryClient();

  return useMutation<None, Error, void>({
    mutationFn: async () => {
      const result = await deleteApiPatientRecentSearches();
      queryClient.setQueryData(recentSearchesQueryKeys.all, []);
      await queryClient.invalidateQueries({
        queryKey: recentSearchesQueryKeys.all,
      });
      return result;
    },
  });
}
